/**
 * Token-aware AgentHubClient factory for pulse-hub.
 *
 * AgentHubClient from @pulse-business-ai/api-client accepts the token only at
 * construction time — there is no setTokenGetter() method on the class.  This
 * module wraps it in a factory that re-creates the client whenever the token
 * changes, keeping the rest of the app oblivious to that detail.
 *
 * Usage
 * -----
 * In App.tsx ApiWiring:
 *   setAgentHubTokenGetter(() => getAccessToken());
 *
 * In feature api.ts files:
 *   import { getAgentHubClient } from '@/core/api/agentHubClient';
 *   getAgentHubClient().callTool<T>('tool_name', args);
 *
 * NOTE: The /chat dispatch+poll flow (useChatDispatch / usePollJobResult)
 * continues to use the local `api` instance directly — that pattern is
 * correct-by-design (async job dispatch, not streamChat).
 */

import { AgentHubClient } from "@pulse-business-ai/api-client";

const baseUrl =
  import.meta.env.VITE_AGENT_HUB_URL ??
  import.meta.env.VITE_API_URL ??
  "https://agent-hub.pulsebusiness.ai";

let cachedClient: AgentHubClient | null = null;
let cachedToken: string | null = null;
let tokenGetter: () => string | null = () => null;

/**
 * Register a token getter.  Called once from App.tsx ApiWiring once the auth
 * context is available.  Invalidates the cached client so the next
 * getAgentHubClient() call picks up a fresh token.
 */
export function setAgentHubTokenGetter(getter: () => string | null): void {
  tokenGetter = getter;
  cachedClient = null; // invalidate so next call re-creates with new token
  cachedToken = null;
}

/**
 * Return the shared AgentHubClient, creating a new one only when the token
 * has changed (or on first call).
 */
export function getAgentHubClient(): AgentHubClient {
  const token = tokenGetter() ?? "";
  if (token !== cachedToken || !cachedClient) {
    cachedClient = new AgentHubClient({
      baseUrl,
      token,
      fetchImpl: (input, init) => globalThis.fetch(input, init),
    });
    cachedToken = token;
  }
  return cachedClient;
}
